import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WeightTagToolbar from '../components/toolbars/WeightTagToolbar';
import '../index.css';

const WeightTag = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [vineyards, setVineyards] = useState([]);
    const [varietals, setVarietals] = useState([]);
    const [weightTag, setWeightTag] = useState({
        date: new Date().toISOString().split('T')[0],
        vineyardID: '',
        varietal: '',
        totalGross: 0, // Default numeric values
        totalTare: 0,
        totalNet: 0,
        totalTons: 0,
        binWeights: Array(18).fill().map((_, index) => ({
            binNumber: index + 1,
            grossWeight: 0, // Default numeric values
            tareWeight: 0,
            netWeight: 0
        }))
    });

    useEffect(() => {
        // Fetch vineyards and varietals for dropdowns
        const fetchData = async () => {
            try {
                const [vineyardsResponse, varietalsResponse] = await Promise.all([
                    axios.get('/api/vineyards'),
                    axios.get('/api/varietals')
                ]);
                
                setVineyards(vineyardsResponse.data);
                setVarietals(varietalsResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
    
        fetchData();
    
        // If editing existing weight tag, fetch its data
        if (id) {
            fetchWeightTag();
        }
    }, [id]);
    // Calculate costs of grapes
    // const calculateCost = async () => {
    //    const response = await axios.get(`/api/grapePrices`, { 
    //        params: { vineyardID, varietal, year: new Date().getFullYear() }
    //    });
    //    const pricePerTon = response.data.pricePerTon;
    //    const totalTons = weightTag.totalTons;
    //   const totalCost = pricePerTon * totalTons;
    //    console.log(`Total Cost: ${totalCost}`);
    //};
    const fetchWeightTag = async () => {
        try {
            const response = await axios.get(`/api/weightTags/${id}`);
            const data = response.data;
    
            // Format bin weights to match state structure
            const formattedBinWeights = Array(18).fill().map((_, index) => {
                const existingBin = data.binWeights.find(bin => bin.binNumber === index + 1);
                return {
                    binNumber: index + 1,
                    grossWeight: Number(existingBin?.grossWeight || 0),
                    tareWeight: Number(existingBin?.tareWeight || 0),
                    netWeight: Number(existingBin?.netWeight || 0)
                };
            });
    
            setWeightTag({
                ...data,
                totalGross: Number(data.totalGross || 0),
                totalTare: Number(data.totalTare || 0),
                totalNet: Number(data.totalNet || 0),
                totalTons: Number(data.totalTons || 0),
                binWeights: formattedBinWeights
            });
        } catch (error) {
            console.error('Error fetching weight tag:', error);
            alert('Error loading weight tag data');
        }
    };

    const calculateNetWeight = (gross, tare) => {
        return Math.max(0, gross - tare);
    };

    const handleBinWeightChange = (index, field, value) => {
        const newBinWeights = [...weightTag.binWeights];
        newBinWeights[index] = {
            ...newBinWeights[index],
            [field]: value
        };

        // Calculate net weight for this bin
        if (field === 'grossWeight' || field === 'tareWeight') {
            newBinWeights[index].netWeight = calculateNetWeight(
                field === 'grossWeight' ? value : newBinWeights[index].grossWeight,
                field === 'tareWeight' ? value : newBinWeights[index].tareWeight
            );
        }

        // Calculate totals
        const totals = newBinWeights.reduce((acc, bin) => ({
            totalGross: acc.totalGross + (Number(bin.grossWeight) || 0),
            totalTare: acc.totalTare + (Number(bin.tareWeight) || 0),
            totalNet: acc.totalNet + (Number(bin.netWeight) || 0)
        }), { totalGross: 0, totalTare: 0, totalNet: 0 });

        // Convert to tons (1 ton = 2000 pounds)
        const totalTons = totals.totalNet / 2000;

        setWeightTag({
            ...weightTag,
            binWeights: newBinWeights,
            ...totals,
            totalTons
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                ...weightTag,
                binWeights: weightTag.binWeights.filter(bin => bin.grossWeight > 0)
            };

            if (id) {
                await axios.put(`/api/weightTags/${id}`, submitData);
            } else {
                await axios.post('/api/weightTags', submitData);
            }
            alert(`Weight Tag ${id ? 'updated' : 'created'} successfully`);
            navigate('/weight-tags');
        } catch (error) {
            console.error('Error saving weight tag:', error);
            alert('Error saving weight tag data');
        } finally {
            setLoading(false);
        }
    };
    const formatDateForInput = (isoDate) => {
        if (!isoDate) return ''; // Handle empty or invalid date
        return isoDate.split('T')[0]; // Extract only the date portion
    };

    const handleEdit = (id) => {
        navigate(`/weight-tags/edit/${id}`);
    };

    const handlePrint = (id) => {
        navigate(`/weight-tags/${id}/print`);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this weight tag?')) {
            try {
                await axios.delete(`/api/weightTags/${id}`);
                alert('Weight tag deleted successfully');
                navigate('/weight-tags');
            } catch (error) {
                console.error('Error deleting weight tag:', error);
                alert('Failed to delete weight tag');
            }
        }
    };

    return (
        <>
            <WeightTagToolbar 
                loading={loading}
                onSave={handleSubmit}
                onPrint={() => handlePrint(id)}
                onNavigateBack={() => navigate('/weight-tags')}
                onDelete={() => handleDelete(id)}
                id={id}
            />
            <div className="container mt-4 mb-4">
                {/* Logo and Header Section */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <img src="http://jeremywineco.com/wp-content/themes/BOX/img/j-logo.png" 
                                     alt="Jeremy Wine Co" 
                                     style={{ height: '100px' }} />
                            </div>
                            <div className="text-center">
                                <h2 className="mb-0">JEREMY WINE CO.</h2>
                                <p className="mb-0">16750 E HWY 88</p>
                                <p className="mb-0">LOCKEFORD, CA 95237</p>
                                <p className="mb-0">209-367-3773 | BWN-16742</p>
                            </div>
                            <div className="text-end">
                                <h2>OFFICIAL WEIGHT TAG</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Form Header Section */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-4">
                                    <label className="form-label">DATE:</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={formatDateForInput(weightTag.date)}
                                        onChange={(e) => setWeightTag({ ...weightTag, date: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">VARIETAL:</label>
                                    <select
                                        className="form-control"
                                        value={weightTag.varietal}
                                        onChange={(e) => setWeightTag({ ...weightTag, varietal: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Varietal</option>
                                        {varietals.map((varietal, index) => (
                                            <option key={index} value={varietal}>
                                                {varietal}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">VINEYARD:</label>
                                    <select
                                        className="form-control"
                                        value={weightTag.vineyardID}
                                        onChange={(e) => setWeightTag({ ...weightTag, vineyardID: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Vineyard</option>
                                        {vineyards.map(vineyard => (
                                            <option key={vineyard.id} value={vineyard.id}>
                                                {vineyard.vineyardName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bin Weights Section */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                {[0, 1, 2].map(row => (
                                    <div key={row} className="col-12 mb-4">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>BIN #</th>
                                        {[1, 2, 3, 4, 5, 6].map(col => (
                                            <th key={col}>{row * 6 + col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>GROSS:</td>
                                        {[0, 1, 2, 3, 4, 5].map(col => (
                                            <td key={col}>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={weightTag.binWeights[row * 6 + col].grossWeight || ''}
                                                    onChange={(e) => handleBinWeightChange(
                                                        row * 6 + col,
                                                        'grossWeight',
                                                        Number(e.target.value)
                                                    )}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>TARE:</td>
                                        {[0, 1, 2, 3, 4, 5].map(col => (
                                            <td key={col}>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={weightTag.binWeights[row * 6 + col].tareWeight}
                                                    onChange={(e) => handleBinWeightChange(
                                                        row * 6 + col,
                                                        'tareWeight',
                                                        Number(e.target.value)
                                                    )}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>NET:</td>
                                        {[0, 1, 2, 3, 4, 5].map(col => (
                                            <td key={col}>
                                                {weightTag.binWeights[row * 6 + col].netWeight}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
                </div>
                </div>

                {/* Totals */}
                <div className="card mb-4">
                        <div className="card-body">
                            <div className="row justify-content-end">
                                <div className="col-md-6">
                                    <table className="table table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th colSpan="2">TOTALS OF WEIGHTS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>GROSS:</td>
                                                <td>{!isNaN(weightTag.totalGross) ? weightTag.totalGross.toFixed(2) : '0.00'}</td>
                                            </tr>
                                            <tr>
                                                <td>TARE:</td>
                                                <td>{!isNaN(weightTag.totalTare) ? weightTag.totalTare.toFixed(2) : '0.00'}</td>
                                            </tr>
                                            <tr>
                                                <td>NET:</td>
                                                <td>{!isNaN(weightTag.totalNet) ? weightTag.totalNet.toFixed(2) : '0.00'}</td>
                                            </tr>
                                            <tr>
                                                <td>TONS:</td>
                                                <td>{!isNaN(weightTag.totalTons) ? weightTag.totalTons.toFixed(3) : '0.00'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                             </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        </>
    );
};

export default WeightTag;